/**
 * Rule editors (mandate §5) — the two dialogs behind the RiskRules screen's live controls.
 *
 * RuleParamEditor sits behind each system rule's row Edit: its inputs save through
 * `prepare.riskRules.update({params})` and the card re-renders from the SERVER's catalogue. A loosened
 * parameter queues (ADR-014) and the toast says so — the card holds the enforced values meanwhile.
 *
 * CustomRuleDialog sits behind Add Rule: it creates an ADVISORY rule — it surfaces on the TradeHub monitor
 * when it fires and counts in the engine panel, and it never gates an order. The dialog says that plainly,
 * because a trader who believes an advisory rule blocks orders is trading on protection they do not have.
 */
import type { ViewModel } from '@/screens/generated/view-model';
import { css } from '@pointfive/ui';

const overlay = css(
  'position:fixed;inset:0;z-index:70;display:flex;align-items:center;justify-content:center;background:color-mix(in srgb, var(--bg) 55%, transparent);backdrop-filter:blur(2px)',
);
const sheet = css(
  'width:380px;max-width:calc(100vw - 40px);border-radius:16px;border:1px solid var(--card-line);background:var(--surface);box-shadow:var(--shadow-float);padding:18px',
);
const field = css(
  'width:100%;padding:9px 12px;border-radius:10px;border:1px solid var(--l10);background:var(--l2);color:var(--text);font-size:12.5px',
);
const label = css(
  'font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:var(--muted2);font-weight:600;margin:10px 0 4px',
);
const ghostBtn = css(
  'font-size:11.5px;padding:7px 14px;border-radius:99px;border:1px solid var(--l10);background:transparent;color:var(--text-mid);cursor:pointer',
);
const primaryBtn = css(
  'font-size:11.5px;font-weight:600;padding:7px 15px;border-radius:99px;border:1px solid color-mix(in srgb, var(--slate-fill) 50%, transparent);background:color-mix(in srgb, var(--slate-fill) 10%, transparent);color:var(--slate);cursor:pointer',
);

export function RuleParamEditor({ m }: { m: Partial<ViewModel> }) {
  const e = m.ruleParamEditorOpen;
  if (!e) return null;
  return (
    <div style={overlay} role="dialog" aria-modal="true" aria-label={`Edit ${e.name} parameters`}>
      <div style={sheet}>
        <h2 style={css('font-size:13px;font-weight:600;margin:0 0 6px')}>{e.name}</h2>
        <p
          id="pf-rule-editor-note"
          style={css('font-size:11.5px;color:var(--text-mid);line-height:1.6;margin:0 0 6px')}
        >
          Tightening applies immediately. Loosening is queued for the refractory delay — the current
          parameters keep enforcing until then.
        </p>
        {e.params.map((p) => (
          <div key={p.k}>
            <div style={label}>{p.k}</div>
            <input
              style={field}
              aria-label={`${e.name}: ${p.k}`}
              aria-describedby="pf-rule-editor-note"
              value={p.v}
              onChange={(ev) => m.ruleParamSet?.(p.k, ev.target.value)}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter') m.ruleParamSave?.();
              }}
            />
          </div>
        ))}
        <div style={css('display:flex;gap:8px;justify-content:flex-end;margin-top:14px')}>
          <button style={ghostBtn} onClick={m.ruleParamCancel}>
            Cancel
          </button>
          <button style={primaryBtn} onClick={m.ruleParamSave} aria-label="Save the rule parameters">
            Save parameters
          </button>
        </div>
      </div>
    </div>
  );
}

export function CustomRuleDialog({ m }: { m: Partial<ViewModel> }) {
  const e = m.customEditorOpen;
  if (!e) return null;
  return (
    <div style={overlay} role="dialog" aria-modal="true" aria-label="Add a custom rule">
      <div style={sheet}>
        <h2 style={css('font-size:13px;font-weight:600;margin:0 0 6px')}>Add Rule</h2>
        <p
          id="pf-custom-rule-note"
          style={css('font-size:11.5px;color:var(--text-mid);line-height:1.6;margin:0 0 6px')}
        >
          Custom rules are advisory: when the condition fires it appears on the trade monitor and in the
          engine panel. It never blocks an order — only the system rules and your risk limits do that.
        </p>
        <div style={label}>Rule name</div>
        <input
          style={field}
          aria-label="Rule name"
          aria-describedby="pf-custom-rule-note"
          maxLength={80}
          value={e.label}
          onChange={(ev) => m.customEditorSet?.('label', ev.target.value)}
        />
        <div style={label}>Watch for</div>
        <input
          style={field}
          aria-label="The condition to watch for"
          placeholder="e.g. Three losses inside an hour on one symbol"
          maxLength={280}
          value={e.expression}
          onChange={(ev) => m.customEditorSet?.('expression', ev.target.value)}
        />
        <div style={label}>Weight</div>
        <select
          style={field}
          aria-label="Advisory weight"
          value={e.severity}
          onChange={(ev) => m.customEditorSet?.('severity', ev.target.value)}
        >
          <option value="soft">Advisory</option>
          <option value="hard">Advisory · strong</option>
        </select>
        <div style={css('display:flex;gap:8px;justify-content:flex-end;margin-top:14px')}>
          <button style={ghostBtn} onClick={m.customEditorCancel}>
            Cancel
          </button>
          <button style={primaryBtn} onClick={m.customEditorSave} aria-label="Add the rule">
            Add rule
          </button>
        </div>
      </div>
    </div>
  );
}
